import React, { useContext, useEffect } from 'react';
import { Redirect, Switch, Route } from 'react-router-dom';
import {
  Typography,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Tooltip,
} from '@material-ui/core';

import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import BuildIcon from '@material-ui/icons/Build';
import { makeStyles } from '@material-ui/core/styles';
import { StarBorderOutlined, ExitToAppOutlined } from '@material-ui/icons';
import { useLocation } from 'react-router';

import { Search, Toast } from '../../components/';
import { OddityReports } from '../OddityReports/OddityReports';
import { SpeakeasyTrades } from '../SpeakeasyTrades/SpeakeasyTrades';
import { BotFiles } from '../Utilities/BotFiles';
import { FC3Screenshots } from '../Utilities/FC3Screenshots';
import { YodleeLookup } from '../Utilities/YodleeLookup';
import { FundReconciliation } from '../Utilities/FundReconciliation';

import { AppContext } from '../../modules/AppContext';
import { logout } from '../../modules/Login';

import logo from '../../logos/futureLogo.svg';
import { AndromedaUtilities } from '../Andromeda';
import { usePermissions } from '../../modules/Permissions';
import { BatchYodleeRelink } from '../Utilities/BatchYodleeRelink';
import { IntegratedTransition } from '../Utilities/IntegratedTransition';
import { CoreToIntegratedTransition } from '../Utilities/CoreToIntegratedTransition';

const drawerWidth = 300;
const toolbarHeight = 80;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    background: '#fff',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  // necessary for content to be below app bar
  toolbar: {
    minHeight: toolbarHeight,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  content: {
    flexGrow: 1,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  logo: {
    width: 200,
    height: toolbarHeight,
    marginLeft: 20,
  },
}));

export const Dashboard = ({ history }) => {
  const { searchToken, setSearchToken } = useContext(AppContext);
  const {
    hasAndromedaAccess,
    hasBatchYodleeRelinkAccess,
    hasIntegratedTransitionAccess,
    hasFundReconciliationAccess,
  } = usePermissions();
  const location = useLocation();

  const handleSearch = (event) => {
    setSearchToken(event.target.value);
  };

  const classes = useStyles();
  const [open, setOpen] = React.useState(true);

  const handleClick = () => {
    setOpen(!open);
  };

  const sidebar = [
    {
      title: 'Trades',
      route: 'trades',
      icon: <LocalAtmIcon />,
      pages: [
        {
          title: 'Oddity Reports',
          route: 'oddity-reports',
        },
        {
          title: 'Speakeasy Trades',
          route: 'speakeasy-trades',
        },
        {
          title: 'Trades',
          route: 'trades',
        },
      ],
    },
    {
      title: 'Utilities',
      route: 'utilities',
      icon: <BuildIcon />,
      pages: [
        {
          title: 'Bot File Downloads',
          route: 'bot-files',
        },
        {
          title: 'FC3 Screenshots',
          route: 'fc3-screenshots',
        },
        {
          title: 'Yodlee Lookup',
          route: 'yodlee-lookup',
        },
        {
          title: 'Batch Yodlee Relink',
          route: 'batch-yodlee-relink',
        },
        {
          title: 'Fund Reconciliation',
          route: 'fund-reconciliation',
          permissions: hasFundReconciliationAccess,
        },
        {
          title: 'Integrated To Integrated Transition Utility',
          route: 'integrated-transition',
          permissions: hasIntegratedTransitionAccess,
        },
        {
          title: 'Core To Integrated Transition Utility',
          route: 'core-to-integrated-transition',
          permissions: hasIntegratedTransitionAccess,
        },
      ],
    },
    {
      title: 'Andromeda',
      route: 'andromeda',
      icon: <StarBorderOutlined />,
      permissions: hasAndromedaAccess,
      pages: [
        { title: 'Agents', route: 'agents' },
        { title: 'Jobs Explorer', route: 'jobs-explorer' },
      ],
    },
  ];

  return (
    <div className={classes.root}>
      <Toast />
      <AppBar
        position="fixed"
        className={classes.appBar}
        elevation={0}
        color="default"
      >
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" noWrap>
            <Search
              value={searchToken}
              onChange={handleSearch}
              onClear={() => setSearchToken('')}
            />
          </Typography>
          <div style={{ marginLeft: 'auto' }}>
            <Tooltip title="Logout">
              <IconButton onClick={logout}>
                <ExitToAppOutlined />
              </IconButton>
            </Tooltip>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="left"
      >
        <img src={logo} alt="future-capital" className={classes.logo} />
        <List>
          {sidebar
            .filter((section) => section.permissions ?? true)
            .map((section, index) => (
              <React.Fragment key={section.title}>
                <ListItem button key={section.route}>
                  <ListItemIcon>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.title} />
                </ListItem>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {section.pages
                      .filter((page) => page.permissions ?? true)
                      .map((page) => {
                        const pageRoute = `/dashboard/${section.route}/${page.route}`;
                        return (
                          <ListItem
                            key={page.title}
                            button
                            selected={location.pathname.endsWith(pageRoute)}
                            className={classes.nested}
                            onClick={() => {
                              history.push(pageRoute);
                            }}
                          >
                            <FiberManualRecordIcon
                              style={{
                                fontSize: '8px',
                                marginLeft: '32px',
                                marginRight: '15px',
                              }}
                            />
                            <ListItemText primary={page.title} />
                          </ListItem>
                        );
                      })}
                  </List>
                </Collapse>
                <Divider />
              </React.Fragment>
            ))}
        </List>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <Switch>
          <Route
            exact
            path="/dashboard/trades/oddity-reports"
            component={OddityReports}
          />
          <Route
            exact
            path="/dashboard/trades/speakeasy-trades"
            component={SpeakeasyTrades}
          />
          <Route
            exact
            path="/dashboard/utilities/bot-files"
            component={BotFiles}
          />
          <Route
            exact
            path="/dashboard/utilities/fc3-screenshots"
            component={FC3Screenshots}
          />
          <Route
            exact
            path="/dashboard/utilities/yodlee-lookup"
            component={YodleeLookup}
          />
          {hasFundReconciliationAccess && (
            <Route
              exact
              path="/dashboard/utilities/fund-reconciliation"
              component={FundReconciliation}
            />
          )}
          {hasBatchYodleeRelinkAccess && (
            <Route
              exact
              path="/dashboard/utilities/batch-yodlee-relink"
              component={BatchYodleeRelink}
            />
          )}
          {hasIntegratedTransitionAccess && (
            <Route
              exact
              path="/dashboard/utilities/integrated-transition"
              component={IntegratedTransition}
            />
          )}
          {hasIntegratedTransitionAccess && (
            <Route
              exact
              path="/dashboard/utilities/core-to-integrated-transition"
              component={CoreToIntegratedTransition}
            />
          )}
          {hasAndromedaAccess && (
            <Route path="/dashboard/andromeda" component={AndromedaUtilities} />
          )}
          <Route path="*" component={() => <div>Coming Soon!</div>} />
          <Redirect to="/dashboard/trades/oddity-reports" />{' '}
          {/* If we get here to the end and have not matched a route, default back to oddity reports */}
        </Switch>
      </main>
    </div>
  );
};
